import { DownOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DATE_FORMAT } from "localConstants.ts";
import { PropsWithChildren, useRef, useState, useImperativeHandle, forwardRef, RefObject, Ref, useEffect } from "react";
import { MouseEvent } from "react";
import { zIndexes } from "utils/zIndexes.ts";

import "components/UI/Table/UI/InteractiveCell/miscs/overrides.css";

export type FieldValueType = string | number | bigint | readonly string[] | undefined | Dayjs | null;

interface FieldWrapperProps<TValue extends FieldValueType> {
    displayValue: TValue;
    isError?: boolean;
    errorMessage?: string;
    isSelect?: boolean;
    overlay?: boolean;
    alwaysEditing?: boolean;
}

export interface FieldWrapperImperativeHandle {
    setIsEditMode: (v: boolean) => void;
    setBlockTriggerOnHover: (v: boolean) => void;
    triggeredByClick: boolean;
    setTriggeredByClick: (v: boolean) => void;
    setIsFocused: (v: boolean) => void;
    elementRef: RefObject<HTMLDivElement>;
}

export const FieldWrapper = forwardRef(function InteractiveCell<TValue extends FieldValueType>(
    { displayValue, children, isError, errorMessage, isSelect, overlay, alwaysEditing = false }: PropsWithChildren<
        FieldWrapperProps<TValue>
    >,
    ref: Ref<FieldWrapperImperativeHandle>
) {
    const elementRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [triggeredByClick, setTriggeredByClick] = useState<boolean>(false);
    const [, setBlockTriggerOnHover] = useState<boolean>(false);
    const [overlayWidth, setOverlayWidth] = useState<number>(320);
    const [overlayPosition, setOverlayPosition] = useState<{
        left?: number;
        right?: number;
        transform?: string;
        top?: string | number;
        bottom?: string;
        height?: number;
    }>({ left: 0 });

    useImperativeHandle(ref, () => ({
        setIsEditMode,
        setBlockTriggerOnHover,
        triggeredByClick,
        setTriggeredByClick,
        setIsFocused: setIsFocus,
        elementRef,
    }));

    useEffect(() => {
        if (alwaysEditing || !overlay || !isEditMode || !elementRef.current) return;

        const rect = elementRef.current.getBoundingClientRect();
        const computedWidth = Math.max(rect.width, 320);
        setOverlayWidth(computedWidth);

        const viewportWidth = window.innerWidth;
        const spaceOnRight = viewportWidth - rect.right;
        const spaceOnLeft = rect.left;

        if (spaceOnRight < computedWidth) {
            if (spaceOnLeft >= computedWidth) {
                setOverlayPosition((prev) => ({ ...prev, right: 0, left: undefined }));
            } else {
                setOverlayPosition((prev) => ({
                    ...prev,
                    left: Math.min(0, viewportWidth - computedWidth - rect.left),
                    right: undefined,
                }));
            }
        } else {
            setOverlayPosition((prev) => ({ ...prev, left: 0, right: undefined }));
        }

        let overflowCell: HTMLElement | null = null;
        let previousOverflow = "";
        let el = elementRef.current.parentNode as HTMLElement;
        while (el && el !== document.body) {
            if (el.tagName === "TD" && el.classList.contains("ant-table-cell-ellipsis")) {
                overflowCell = el;
                previousOverflow = el.style.overflow;
                el.style.overflow = "visible";
                break;
            }
            el = el.parentNode as HTMLElement;
        }

        const tableWrapper = elementRef.current.closest(".ant-table-wrapper") as HTMLElement;
        // No `.ant-table-pagination` exists when the table renders with `pagination={false}`
        // (e.g. Pingtree, which loads everything at once) — fall back to the scrollable table
        // body's own bottom edge as the boundary so overlays on the last row still flip upward.
        const pagination = tableWrapper?.querySelector(".ant-table-pagination") as HTMLElement | null;
        const tableBody = tableWrapper?.querySelector(".ant-table-tbody") as HTMLElement;

        if (tableWrapper && tableBody) {
            const cellRect = elementRef.current.getBoundingClientRect();
            const boundary = pagination
                ? pagination.getBoundingClientRect().top
                : tableBody.getBoundingClientRect().bottom;
            const tableHeaderRect = tableWrapper.querySelector(".ant-table-thead")?.getBoundingClientRect();
            const overlayHeight = 56;
            const overlayBottom = cellRect.bottom + overlayHeight;
            const overlayTop = cellRect.top - overlayHeight;

            if (overlayBottom <= boundary) {
                setOverlayPosition((prev) => ({ ...prev, height: undefined, transform: undefined }));
            } else if (tableHeaderRect ? overlayTop >= tableHeaderRect.bottom : overlayTop >= 0) {
                setOverlayPosition((prev) => ({
                    ...prev,
                    transform: `translateY(-100%)`,
                    top: 0,
                    height: overlayHeight,
                }));
            } else {
                setOverlayPosition((prev) => ({
                    ...prev,
                    transform: `translateY(-50%)`,
                    top: "50%",
                    height: overlayHeight,
                }));
            }
        }

        return () => {
            if (overflowCell) {
                overflowCell.style.overflow = previousOverflow;
            }
        };
    }, [alwaysEditing, overlay, isEditMode]);

    useEffect(() => {
        if (!overlay) return;

        const handleClickOutside = (event: globalThis.MouseEvent) => {
            if (
                event.target instanceof Element &&
                (event.target.closest(".ant-select-dropdown") ||
                    event.target.closest(".ant-select-item") ||
                    event.target.closest(".ant-popover"))
            ) {
                return;
            }

            if (
                overlayRef.current &&
                !overlayRef.current.contains(event.target as Node) &&
                elementRef.current &&
                !elementRef.current.contains(event.target as Node)
            ) {
                setIsEditMode(false);
                setTriggeredByClick(false);
            }
        };

        if (isEditMode) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [overlay, isEditMode]);

    const handleValueKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (isEditMode) {
            return;
        }

        if (e.key === "Enter" || e.key === "Space") {
            setIsEditMode(true);
            e.preventDefault();
            setIsEditMode(true);
            setBlockTriggerOnHover(true);
            setTriggeredByClick(true);
        }
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if (isEditMode) {
            if (overlay) {
                event?.stopPropagation();
                setIsEditMode(false);
                setTriggeredByClick(false);
                setIsFocus(false);
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                return;
            }
            return;
        }

        event?.stopPropagation();
        setIsEditMode(true);
        setBlockTriggerOnHover(true);
        setTriggeredByClick(true);
    };

    const handleMouseEnter = () => {
        // Click-only: don't trigger edit mode on hover
    };

    const handleMouseLeave = () => {
        if (!isFocus && !triggeredByClick && (displayValue !== undefined || displayValue !== null)) {
            setTimeout(() => setIsEditMode(false), 200);
        }

        setBlockTriggerOnHover(false);
    };

    const displayContent = (
        <span
            role="button"
            tabIndex={0}
            onKeyDown={handleValueKeyDown}
            className={`appearance-none bg-transparent border border-transparent min-w-full text-left focus:outline-none focus:border-0 focus:border-[#008080] focus:rounded p-1
    ${!overlay && isEditMode ? "invisible" : ""}
    ${isError ? "border-2 border-[#EF4444] rounded" : ""}
   `}
        >
            <div className={"flex flex-col w-full"}>
                <div className={"flex items-center justify-between w-full"}>
                    <span>
                        {resolveValue(displayValue)} {isSelect && <DownOutlined className="ml-2 text-xs" />}
                    </span>
                </div>
                <span className={"text-[#EF4444]"}>{errorMessage && errorMessage}</span>
            </div>
        </span>
    );

    if (alwaysEditing) {
        return (
            <div ref={elementRef} className="flex w-full flex-col">
                {children}
                {errorMessage && <span className="text-[#EF4444]">{errorMessage}</span>}
            </div>
        );
    }

    // TODO refactor text-[#008080] to class (whole project color refactor)
    return (
        <div
            ref={elementRef}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`relative flex flex-1 items-center w-full h-full rounded hover:bg-neutral-200 text-[#008080]`}
        >
            {/* Always render display value to maintain consistent row height */}
            {displayContent}
            {/* Edit controls */}
            {overlay && isEditMode ? (
                <div
                    ref={overlayRef}
                    className="absolute"
                    style={{
                        top: overlayPosition.top ?? "100%",
                        bottom: overlayPosition.bottom,
                        left: overlayPosition.left,
                        right: overlayPosition.right,
                        transform: overlayPosition.transform,
                        marginTop: overlayPosition.transform ? undefined : "2px",
                        marginBottom: overlayPosition.transform ? "2px" : undefined,
                        width: overlayWidth,
                        height: overlayPosition.height,
                        zIndex: zIndexes.payoutFormulaWrapper,
                        backgroundColor: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.5rem",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        padding: "0.5rem",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {children}
                </div>
            ) : !overlay && isEditMode ? (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10 min-w-full">{children}</div>
            ) : null}
        </div>
    );
});

const resolveValue = (displayValue: FieldValueType) => {
    if (dayjs.isDayjs(displayValue)) {
        return dayjs(displayValue).format(DATE_FORMAT);
    }

    if (typeof displayValue === "bigint") {
        return displayValue.toString();
    }

    if (displayValue === null || displayValue === undefined) {
        return "-";
    }

    return displayValue;
};
